import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFacilityContext } from '../../contexts/FacilityContext';
import { getUserFacilityId } from '../../utils/userHelpers';
import {
  getAllowedRolesForRoute,
  isFacilityScopedRoute,
  type AppRoutePath,
  type UserRole,
} from '../../config/accessControl';

export interface FacilityRouteGuardProps {
  children: React.ReactNode;
  /**
   * Route template path (e.g. `/facility/residents`). Allowed roles and
   * facility scope are resolved from the shared access-control config.
   */
  routePath: AppRoutePath | string;
  /**
   * Optional override. Prefer `routePath` so roles stay in sync with config.
   * Kept for tests / rare one-offs.
   */
  allowedRoles?: readonly UserRole[] | string[];
  requireFacility?: boolean;
}

/**
 * Stable module-level route guard. Must NOT be declared inside App (or any parent
 * that re-renders on toast/auth changes) — inline definitions remount all children.
 *
 * Facility-scoped routes require facility context for non-admin users.
 * Global routes should use RoleRouteGuard instead.
 */
const FacilityRouteGuard: React.FC<FacilityRouteGuardProps> = ({
  children,
  routePath,
  allowedRoles,
  requireFacility,
}) => {
  const { user } = useAuth();
  const { selectedFacilityId, availableFacilities } = useFacilityContext();

  const roles = allowedRoles ?? getAllowedRolesForRoute(routePath);
  const shouldRequireFacility =
    requireFacility ?? isFacilityScopedRoute(routePath);

  if (!user || !(roles as readonly string[]).includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.role === 'admin') {
    return <>{children}</>;
  }

  if (shouldRequireFacility) {
    const hasFacilityAccess =
      getUserFacilityId(user) || selectedFacilityId || availableFacilities.length > 0;

    if (!hasFacilityAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default FacilityRouteGuard;
